import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface OpenArgs {
  source: string;
  refSuffix?: string;
}

interface DemoAccessContextValue {
  requestDemoAccess: (args: OpenArgs) => void;
}

const DemoAccessContext = createContext<DemoAccessContextValue | null>(null);

export const useDemoAccess = (): DemoAccessContextValue => {
  const ctx = useContext(DemoAccessContext);
  if (!ctx) {
    return { requestDemoAccess: () => {} };
  }
  return ctx;
};

const DemoAccessDialog = lazy(() =>
  import("@/components/DemoAccessDialog").then((module) => ({ default: module.DemoAccessDialog }))
);

export function DemoAccessProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<OpenArgs>({ source: "unknown" });

  const requestDemoAccess = useCallback((args: OpenArgs) => {
    setSource(args);
    setOpen(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "request") {
      requestDemoAccess({ source: "magic-link-error" });
      params.delete("demo");
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, [requestDemoAccess]);

  const value = useMemo(() => ({ requestDemoAccess }), [requestDemoAccess]);

  return (
    <DemoAccessContext.Provider value={value}>
      {children}
      {open && (
        <Suspense fallback={null}>
          <DemoAccessDialog
            open={open}
            onOpenChange={setOpen}
            source={source.source}
            refSuffix={source.refSuffix}
          />
        </Suspense>
      )}
    </DemoAccessContext.Provider>
  );
}
